/**
 * Schémas Zod — Submissions & uploads.
 */

import { z } from "zod";

import { SUBMISSION_STATUSES } from "../constants.js";

// 9.V.2c-2 (F) — code assureur validé SYNTAXIQUEMENT (slug) ici ; la validation
// MÉTIER (assureur actif du tenant) se fait côté route au point de persistance
// (une submission = réponse d'assureur = sélection contraignante). Jamais de DB
// depuis Zod.
import { InsurerCodeSchema } from "./insurers.js";

/**
 * Chunk 9.L.7 D42 finition — `quote_request_id` + `received_via_intake_event_id`
 * existaient en DB depuis migration 0021 (ADR 0023 §2.3) mais étaient absents
 * côté Zod. Optionnels nullable :
 *   - `quote_request_id` rattache la submission à la quote_request sortante
 *     (NULL = submission spontanée hors quote_request, cas legacy/intake direct).
 *   - `received_via_intake_event_id` trace l'intake_event public qui a
 *     matérialisé cette submission (NULL = saisie directe broker / extraction
 *     manuelle pré-9.G.1).
 * Backend handler valide tenant + project consistency entre la submission et
 * le quote_request référencé.
 */
export const CreateSubmissionSchema = z.object({
  project_id: z.string().uuid(),
  insurer_code: InsurerCodeSchema,
  insurer_name: z.string().min(2).max(100),
  detection_confidence: z.number().min(0).max(1).optional(),
  quote_request_id: z.string().uuid().optional(),
  /**
   * Le créneau assureur auquel CETTE proposition répond — 9.AO.1.
   *
   * C'est le lien **authoritative** de 9.AG.1. `quote_request_id` ci-dessus dit
   * à quelle DEMANDE la proposition se rattache ; lui seul dit à quel
   * DESTINATAIRE, et c'est ce qui compte quand un même assureur a été sollicité
   * par plusieurs demandes — huit couples sont dans ce cas.
   *
   * Il manquait à ce schéma, et c'est la raison mesurée pour laquelle **0 des 15
   * propositions** le portent : le seul chemin d'écriture était la consignation
   * d'une réponse assureur, geste que ce corpus n'a jamais exercé. Sans lui, la
   * boucle assureur, le filtre par demande du comparateur et la demande
   * d'information fonctionnent mais ne servent à rien.
   *
   * Optionnel : une proposition peut arriver hors de toute demande (dépôt direct,
   * document reçu sans sollicitation). L'exiger casserait ces parcours.
   */
  responds_to_recipient_id: z.string().uuid().optional(),
  received_via_intake_event_id: z.string().uuid().optional(),
});
export type CreateSubmissionInput = z.infer<typeof CreateSubmissionSchema>;

export const UpdateSubmissionSchema = z.object({
  status: z.enum(SUBMISSION_STATUSES).optional(),
  insurer_code: InsurerCodeSchema.optional(),
  notes: z.string().max(5000).optional(),
  flags: z.array(z.string().max(50)).max(20).optional(),
});

export type UpdateSubmissionInput = z.infer<typeof UpdateSubmissionSchema>;

// -----------------------------------------------------------------------------
// Filtres listing
// -----------------------------------------------------------------------------
export const SubmissionFilterSchema = z.object({
  project_id: z.string().uuid().optional(),
  status: z.enum(SUBMISSION_STATUSES).optional(),
  insurer_code: InsurerCodeSchema.optional(),
});
export type SubmissionFilter = z.infer<typeof SubmissionFilterSchema>;

/**
 * Corps de `PATCH /v1/submissions/:id/recipient-link` — chunk 9.AP.1.
 *
 * Un seul champ, et sa valeur nulle porte le détachement : `null` signifie
 * « cette proposition ne répond à aucune demande ». Deux routes séparées auraient
 * donné deux endroits où le créneau peut changer de main, et deux occasions de
 * diverger sur ce qu'on rend au créneau libéré.
 *
 * `nullable()` et non `optional()` : omettre le champ n'est pas la même chose que
 * demander un détachement, et le schéma doit refuser l'ambiguïté plutôt que de
 * choisir pour l'appelant.
 */
export const UpdateSubmissionRecipientLinkSchema = z
  .object({
    recipient_id: z.string().uuid().nullable(),
  })
  .strict();
export type UpdateSubmissionRecipientLinkInput = z.infer<
  typeof UpdateSubmissionRecipientLinkSchema
>;

export const UploadFileMetaSchema = z.object({
  project_id: z.string().uuid().optional(),
  submission_id: z.string().uuid().optional(),
  company_id: z.string().uuid().optional(),
  kind: z.enum(["submission", "client-request", "attachment"]),
  file_name: z.string().min(1).max(255),
  size_bytes: z
    .number()
    .int()
    .positive()
    .max(50 * 1024 * 1024), // 50 MB
  mime_type: z.literal("application/pdf"),
  sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
});
export type UploadFileMeta = z.infer<typeof UploadFileMetaSchema>;
